// QR Kiosk v2 — §7.2 full-screen per-field text editor takeover (Title / Description /
// Technical notes). Tapping a KFieldTrigger row opens this below the 50px chrome with a
// large 17px textarea, char counter, and autofocus (caret at end). "Uložiť" commits the
// draft into react-hook-form STATE ONLY (field.onChange) — NOTHING hits the API here.
// Persistence happens on the form's own Odoslať/Uložiť submit. Do NOT "fix" this into a
// PATCH call. "Späť" closes and discards the draft.
// Round-6: PORTALED into the kiosk overlay root (escapes the CreateReportV2 <form
// className="kv2-enter"> animated subtree that made this dialog document-anchored — root
// cause A) and `paddingBottom: keyboardInset` (useKeyboardInset — root cause B) so the
// in-flow Uložiť/Späť bar always rides above the keyboard.
import React, { useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../shared/hooks/useTranslation";
import { KCHROME_H } from "../shared/kioskNav";
import { usePressed } from "../shared/usePressed";
import { useKioskOverlayRoot } from "../shared/kioskOverlayRoot";
import { useKeyboardInset } from "../shared/useKeyboardInset";
import { KFieldTrigger } from "./formAtoms";

const REQ_RED = "color-mix(in oklab, var(--red) 80%, var(--ink))";
const AMBER_INK = "color-mix(in oklab, var(--amber) 80%, var(--ink-2))";

interface FieldEditorProps {
  label: string;
  required?: boolean;
  initial: string;
  maxLength: number;
  placeholder: string;
  singleLine?: boolean;
  onSave: (draft: string) => void;
  onClose: () => void;
}

function FieldEditor({
  label,
  required,
  initial,
  maxLength,
  placeholder,
  singleLine,
  onSave,
  onClose,
}: FieldEditorProps) {
  const { translate } = useTranslation("public");
  const { translate: tCommon } = useTranslation("common");
  const { isPhone, isTablet } = useBreakpoint();
  const overlayRoot = useKioskOverlayRoot();
  const { keyboardInset } = useKeyboardInset();
  const [draft, setDraft] = useState(initial);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const savePress = usePressed();

  const columnMaxWidth: number | string = isPhone ? "100%" : isTablet ? 560 : 640;
  const len = draft.length;
  const counterColor = len >= maxLength ? REQ_RED : len >= maxLength * 0.9 ? AMBER_INK : "var(--muted)";

  const commit = () => onSave(singleLine ? draft.replace(/\s*\n+\s*/g, " ").trim() : draft);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (singleLine && e.key === "Enter") {
      e.preventDefault();
      commit();
    }
  };

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal container={overlayRoot}>
        <Dialog.Overlay
          style={{ position: "fixed", top: KCHROME_H, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", zIndex: 40 }}
        />
        <Dialog.Content
          aria-describedby={undefined}
          className="kv2-sheet-up"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            const ta = taRef.current;
            if (ta) {
              ta.focus();
              const n = ta.value.length;
              ta.setSelectionRange(n, n);
            }
          }}
          style={{
            position: "fixed",
            top: KCHROME_H,
            bottom: 0,
            left: 0,
            right: 0,
            paddingBottom: keyboardInset,
            zIndex: 41,
            maxWidth: columnMaxWidth,
            margin: "0 auto",
            background: "var(--card)",
            borderTop: "1px solid var(--border)",
            display: "flex",
            flexDirection: "column",
          }}
        >
        {/* Header — label + live char counter (mono, regular weight — never italic).
            Round-6: the counter is hidden until the first character (an empty field shows
            the placeholder alone, no `0 / N` noise). */}
        <div style={{ flexShrink: 0, padding: "14px 16px 6px", display: "flex", alignItems: "baseline", gap: 10 }}>
          <Dialog.Title asChild>
            <div style={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>
              {label}
              {required && <span style={{ color: REQ_RED }}> *</span>}
            </div>
          </Dialog.Title>
          {len > 0 && (
            <span
              style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, fontWeight: 600, color: counterColor, flexShrink: 0 }}
            >
              {`${len} / ${maxLength}`}
            </span>
          )}
        </div>

        {/* Textarea — comfortable long-text reading (§7.2 whole point). */}
        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          maxLength={maxLength}
          placeholder={placeholder}
          style={{
            flex: 1,
            border: "none",
            background: "transparent",
            outline: "none",
            padding: "8px 16px",
            fontFamily: "inherit",
            fontSize: 17,
            fontWeight: 500,
            lineHeight: 1.55,
            color: "var(--ink)",
            resize: "none",
            width: "100%",
            colorScheme: "light",
          }}
        />

        {/* Bottom action bar — in-flow footer. Round-6: it rides above the keyboard because
            the Content root reserves `paddingBottom: keyboardInset` (useKeyboardInset), NOT
            because of any resizes-visual assumption (that assumption was wrong; see §0). */}
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            gap: 10,
            background: "var(--card)",
            borderTop: "1px solid var(--border)",
            padding: "10px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              height: 54,
              borderRadius: 12,
              border: "1px solid var(--border)",
              background: "var(--bg)",
              color: "var(--ink-2)",
              fontFamily: "inherit",
              fontSize: 15.5,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {translate("kioskBack")}
          </button>
          <button
            type="button"
            {...savePress.pressHandlers}
            onClick={commit}
            style={{
              flex: 1.6,
              height: 54,
              borderRadius: 12,
              border: "none",
              background: "var(--accent)",
              color: "var(--tier-accent-fg)",
              fontFamily: "inherit",
              fontSize: 16,
              fontWeight: 700,
              cursor: "pointer",
              ...savePress.pressStyle,
            }}
          >
            {tCommon("buttons.save")}
          </button>
        </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface EditableFieldProps {
  label: string;
  required?: boolean;
  value?: string;
  onChange: (v: string) => void;
  maxLength: number;
  placeholder: string;
  lines?: number; // 1 = title trigger; 3 = description/notes preview clamp
  singleLine?: boolean; // title: strip newlines + Enter commits
  error?: boolean;
}

/** Read-only trigger row + its own full-screen editor takeover (Controller-friendly). */
export function EditableField({
  label,
  required,
  value,
  onChange,
  maxLength,
  placeholder,
  lines,
  singleLine,
  error,
}: EditableFieldProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <KFieldTrigger value={value} placeholder={placeholder} error={error} lines={lines} onOpen={() => setOpen(true)} />
      {open && (
        <FieldEditor
          label={label}
          required={required}
          initial={value ?? ""}
          maxLength={maxLength}
          placeholder={placeholder}
          singleLine={singleLine}
          onSave={(draft) => {
            onChange(draft);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export default EditableField;
